<script setup lang="ts">
import { computed } from 'vue'

import { useProductGroup } from '../../../composables/use-product-group'
import { useProductStructuredData } from '../../../composables/use-product-structured-data'
import { useCdnImages } from '../../../composables/use-cdn-images'

import type { UnifiedProduct } from '../../../composables/use-product-group'

const props = withDefaults(defineProps<{
  productGroupType?: string
  product: UnifiedProduct
  productIndex?: number
  id?: string | boolean
  isPreview?: boolean
  showRanking?: boolean
  isContentCommerce?: boolean
  noExtendedInformationBelow?: boolean
  reserveSpaceForLabelShoppingDays?: boolean
  reserveSpaceForLabel?: boolean
}>(), {
  productGroupType: 'single',
  productIndex: 0,
  id: false,
  isPreview: false,
  showRanking: false,
  noExtendedInformationBelow: false,
  reserveSpaceForLabelShoppingDays: false,
  reserveSpaceForLabel: true
})

const { productStyleClasses, formatCurrencyString } = useProductGroup({
  productGroupType: () => props.productGroupType
})

const { hasStructuredData, structuredData } = useProductStructuredData({
  product: () => props.product,
  isContentCommerce: () => props.isContentCommerce,
  isPreview: () => props.isPreview
})

const { cdnImageUrl } = useCdnImages()

const productTagSpecialAttributes = computed(() => ({
  href: props.product.link.url,
  target: props.product.link.target,
  rel: props.product.link.rel
}))

const isMultiProductLayout = computed(() => !['autocomparison', 'single'].includes(props.productGroupType))

const primeLogo = cdnImageUrl('/images/prime.png')
</script>

<template>
  <a v-bind="productTagSpecialAttributes"
     :id="typeof id === 'string' ? id : undefined"
     :data-product-type="product.type"
     :data-ga-coco-group-type="productGroupType"
     :data-product-id="product.id"
     :data-ga-coco-unique-id="product.uniqueId"
     :class="productStyleClasses">
    <div class="bx-product__wrapper">
      <div v-if="reserveSpaceForLabel"
           :class="{
             [`bx-product__label`]: true,
             [`bx-product__label--no`]: !product.label
           }">
        <div :class="{
               'bx-product__label-flag': isMultiProductLayout,
               'bx-product__label-flag--empty': isMultiProductLayout && !product.label
             }"
             data-tc="product-label">
          <span v-if="product.label">{{ product.label }}</span>
          <span v-else>&nbsp;</span>
        </div>
        <span v-if="showRanking"
              class="bx-product__rank">
          {{ productIndex + 1 }}
        </span>
      </div>
      <div v-if="reserveSpaceForLabelShoppingDays"
           :class="{
             [`bx-product__label`]: true,
             [`bx-product__label--no`]: !product.shoppingDaysLabel,
             [`bx-product__label--shopping-days`]: true
           }"
           data-tc="product-shopping-days-label">
        <div>
          <span v-if="product.shoppingDaysLabel">{{ product.shoppingDaysLabel }}</span>
          <span v-else>&nbsp;</span>
        </div>
      </div>
      <div class="bx-product__content">
        <figure v-if="product.image"
                class="bx-product__image"
                data-tc="product-image">
          <img :src="product.image"
               loading="lazy"
               width="200"
               height="200"
               :alt="product.title">
        </figure>
        <div class="bx-product__inner">
          <div class="bx-product__main">
            <span class="bx-product__title"
                  data-tc="product-title">
              {{ product.title }}
            </span>
            <div v-if="product.showPrime"
                 class="bx-product__prime">
              <img :src="primeLogo"
                   loading="lazy"
                   width="54"
                   height="32"
                   alt="Amazon Prime">
            </div>
          </div>
          <div class="bx-product__footer">
            <div v-if="!product.outOfStock"
                 :class="{
                   'bx-product__pricing': true,
                   'bx-product__pricing--hidden': !product.showPrice
                 }"
                 data-tc="product-pricing">
              <span v-if="product.sellingPrice"
                    class="bx-product__sp"
                    data-tc="product-sp">
                {{ formatCurrencyString(product.sellingPrice) }}
              </span>
              <div v-if="product.listPrice"
                   class="bx-product__pricing-inner">
                <span class="bx-product__lp"
                      data-tc="product-lp">
                  {{ formatCurrencyString(product.listPrice) }}
                </span>
                <span v-if="product.saving && product.saving >= 10"
                      class="bx-product__saving"
                      data-tc="product-saving">
                  - {{ product.saving }}%
                </span>
              </div>
              <div v-if="product.pricePerUnit"
                   class="bx-product__ppu">
                {{ product.pricePerUnit }}
              </div>
            </div>
            <span class="bx-product__roofline"
                  data-tc="product-roofline">{{ product.roofline }}</span>
            <div :class="['bx-product__button', {
                   'bx-product__button--oos': product.outOfStock,
                   'bx-product__button--space-below': noExtendedInformationBelow
                 }]"
                 data-tc="product-button">
              <span>{{ product.button }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div v-if="hasStructuredData"
         :key="`structured-data-${productIndex}`"
         v-html="structuredData" />
  </a>
</template>
